'use client';

import { useEffect, useRef, type ReactNode } from 'react';

interface NavDropdownProps {
  trigger: ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  children: ReactNode;
  align?: 'left' | 'right';
  label: string;
  wide?: boolean;
}

export function NavDropdown({ trigger, isOpen, onToggle, onClose, children, align = 'left', label, wide }: NavDropdownProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-label={label}
        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
      >
        {trigger}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          <path d="M2.5 4 L5 6.5 L7.5 4" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop to catch clicks outside */}
          <div className="fixed inset-0 z-40" onClick={onClose} />

          <div
            ref={panelRef}
            role="listbox"
            className={`absolute top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-xl border border-border-subtle bg-surface-elevated shadow-lg ${
              wide ? 'min-w-[240px]' : 'min-w-[180px]'
            } ${align === 'right' ? 'right-0' : 'left-0'}`}
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
}

interface NavDropdownItemProps {
  icon?: ReactNode;
  label: string;
  subLabel?: string;
  isActive?: boolean;
  onClick?: () => void;
  href?: string;
  className?: string;
}

export function NavDropdownItem({ icon, label, subLabel, isActive, onClick, href, className: extraClassName }: NavDropdownItemProps) {
  const className = `flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-surface-hover ${
    isActive ? 'bg-surface-hover font-medium text-text-primary' : 'text-text-secondary'
  } ${extraClassName || ''}`;

  const content = (
    <>
      {icon && <span className="text-base">{icon}</span>}
      <span className="flex flex-col">
        <span className="truncate">{label}</span>
        {subLabel && <span className="text-xs text-muted">{subLabel}</span>}
      </span>
      {isActive && (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="ml-auto shrink-0 text-brand">
          <path d="M3 7.5 L5.5 10 L11 4" />
        </svg>
      )}
    </>
  );

  if (href) {
    return (
      <a href={href} onClick={onClick} role="option" aria-selected={isActive} className={className}>
        {content}
      </a>
    );
  }

  return (
    <button onClick={onClick} role="option" aria-selected={isActive} className={className}>
      {content}
    </button>
  );
}
